import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { CalibrationChecklist } from '../components/CalibrationChecklist';
import { VideoPanel } from '../components/VideoPanel';
import { WarningSoundControl } from '../components/WarningSoundControl';
import { calibrationConfig } from '../config/calibration';
import { useExam } from '../context/ExamContext';
import { useCameraStream } from '../hooks/useCameraStream';
import { useVideoCalibration } from '../hooks/useVideoCalibration';

export function CameraSetupPage() {
  const { stream, error } = useCameraStream();
  const { completeCalibration } = useExam();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const calibration = useVideoCalibration(videoRef, stream, {
    mode: 'laptop',
    microphoneTrack: stream?.getAudioTracks()[0]
  });
  const items = useMemo(() => calibrationConfig.publicDemo.mobileCameraRequired ? calibration.items : [
    ...calibration.items,
    { key: 'secondary', label: 'Secondary camera', mandatory: false, state: 'not_required' as const, guidance: 'A phone camera is optional for this public demo.' }
  ], [calibration.items]);

  function continueTo(path: string) {
    if (!calibration.ready) return;
    completeCalibration(calibration.items.filter((item) => item.mandatory && item.state === 'passed').map((item) => item.key));
    navigate(path);
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-12">
      <h1 className="text-3xl font-bold">Camera Setup & Calibration</h1>
      <p className="mt-2 text-slate-600 dark:text-slate-300">Confirm framing, lighting, microphone, and connection health before starting the exam.</p>
      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div>
          <VideoPanel stream={stream} label="Laptop camera preview" videoRef={videoRef} />
          {error ? <p role="alert" className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">{error} Check browser camera and microphone permissions, then reopen setup.</p> : null}
          <div className="mt-5"><WarningSoundControl /></div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <CalibrationChecklist items={items} />
          <div className="mt-5 grid gap-3">
            <Button variant="secondary" onClick={calibration.retry}>Retry Checks</Button>
            <Button className="w-full" disabled={!calibration.ready} onClick={() => continueTo('/phone-pairing')}>Continue to Phone Setup</Button>
            <Button variant="secondary" className="w-full" disabled={!calibration.ready || calibrationConfig.publicDemo.mobileCameraRequired} onClick={() => continueTo('/exam')}>Start Without Phone</Button>
          </div>
          {!calibration.ready ? <p className="mt-3 text-xs text-slate-400">All required system checks must pass before the exam can start.</p> : null}
        </div>
      </div>
    </section>
  );
}
